import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import {
  EpocusReviewAgreement,
  type EpocusReviewSyncPushDocument,
  type QueryPullEpocusReviewArgs,
} from "elephant-web/gql";
import { useEffect, useMemo, useState } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import { RXDB_EPOCUS_EXAM_COLLECTION_NAME } from "../collections/epocus-exam";
import type { EpocusReview } from "../collections/epocus-review";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusReviewPullDocument,
  SyncEpocusReviewPushDocument,
  type WholeSyncableEpocusReviewFragment,
} from "./__generated__/epocus-review.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  EPOCUS_REVIEW_PULL_ROLES,
  EPOCUS_REVIEW_PUSH_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

const GQL_AGREEMENT_BY_RX: Record<
  EpocusReview["agreement"],
  EpocusReviewAgreement | null
> = {
  agree: EpocusReviewAgreement.Agree,
  minor_discrepancy: EpocusReviewAgreement.MinorDiscrepancy,
  major_discrepancy: EpocusReviewAgreement.MajorDiscrepancy,
  disagree: null,
};

const RX_AGREEMENT_BY_GQL: Record<
  EpocusReviewAgreement,
  EpocusReview["agreement"]
> = {
  [EpocusReviewAgreement.Agree]: "agree",
  [EpocusReviewAgreement.MinorDiscrepancy]: "minor_discrepancy",
  [EpocusReviewAgreement.MajorDiscrepancy]: "major_discrepancy",
};

const useEncounterIdOfExam = (
  examId: string | null,
  db?: RxDatabase<RxDBCollections>,
): string | null => {
  const [encounterId, setEncounterId] = useState<string | null>(null);
  const exams = db?.[RXDB_EPOCUS_EXAM_COLLECTION_NAME];

  useEffect(() => {
    if (!examId || !exams) {
      setEncounterId(null);
      return;
    }

    const subscription = exams
      .findOne({ selector: { id: { $eq: examId } } })
      .$.subscribe((doc) => setEncounterId(doc?.encounterId ?? null));

    return () => subscription.unsubscribe();
  }, [examId, exams]);

  return encounterId;
};

export const useReplicateEpocusReview = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusReview>;
}) => {
  const reviewEncounterMatch = useMatch(
    "/epocus/review/encounter/:encounterId",
  );
  const reviewMatch = useMatch("/epocus/review/:examId");
  const performerMatch = useMatch(
    "/epocus/encounters/:encounterId/exams/:examId",
  );
  const examId =
    reviewMatch?.params.examId ?? performerMatch?.params.examId ?? null;

  const examEncounterId = useEncounterIdOfExam(
    reviewMatch?.params.examId ?? null,
    db,
  );
  const encounterId =
    reviewEncounterMatch?.params.encounterId ?? examEncounterId;

  const role = useLazyUser()?.role;
  const pullAllowed = isReplicationRoleAllowed(EPOCUS_REVIEW_PULL_ROLES, role);
  const pushAllowed = isReplicationRoleAllowed(EPOCUS_REVIEW_PUSH_ROLES, role);

  const push = useMemo(
    () => ({
      mutation: SyncEpocusReviewPushDocument,
      mapRxToGql: mapRxEpocusReviewToGqlInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableEpocusReviewFragment> | undefined
  >(() => {
    const filters = encounterId ? { encounterId } : examId ? { examId } : null;
    const entityId = encounterId ?? examId;
    if (!filters || !entityId || !pullAllowed) return undefined;
    return {
      query: SyncEpocusReviewPullDocument,
      reSyncInterval: { minutes: 5 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullEpocusReviewArgs,
        WholeSyncableEpocusReviewFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: { limit: batchSize, filters, checkpoint },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId,
        db,
        collectionName: collection?.name,
      }),
    };
  }, [encounterId, examId, pullAllowed, db, collection]);

  return useGqlReplication<
    EpocusReview,
    WholeSyncableEpocusReviewFragment,
    EpocusReviewSyncPushDocument
  >({
    db,
    collection,
    pull,
    push: pushAllowed ? push : undefined,
    mapGqlToRx: mapGqlEpocusReviewToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-review",
  });
};

export const mapGqlEpocusReviewToRx = (
  doc: WholeSyncableEpocusReviewFragment,
): WithDeleted<EpocusReview> => {
  const mapped = {
    id: doc.id,
    examId: doc.examId,
    reviewerId: doc.meta.createdByUserId,
    agreement: doc.agreement
      ? RX_AGREEMENT_BY_GQL[doc.agreement]
      : doc.agree
        ? "agree"
        : "disagree",
    imageQualityScore: doc.imageQualityScore ?? null,
    qualityScore: doc.qualityScore,
    signedAt: doc.signedAt ?? null,
    feedbackTags: [...(doc.tags ?? [])],
    comment: doc.comment ?? null,
    meta: {
      createdAt: doc.meta.createdAt,
      updatedAt: doc.meta.updatedAt,
      createdByUserId: doc.meta.createdByUserId,
      updatedByUserId: doc.meta.updatedByUserId,
    },
    _deleted: doc.meta.deleted,
  };

  return mapped as WithDeleted<EpocusReview>;
};

export const mapRxEpocusReviewToGqlInput = (
  doc: WithDeleted<EpocusReview>,
): EpocusReviewSyncPushDocument => ({
  id: doc.id,
  examId: doc.examId,
  agreement: GQL_AGREEMENT_BY_RX[doc.agreement],
  agree: doc.agreement === "agree",
  imageQualityScore: doc.imageQualityScore,
  qualityScore: doc.qualityScore,
  signedAt: doc.signedAt,
  tags: doc.feedbackTags,
  comment: doc.comment,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableEpocusReview on SyncableEpocusReview {
    id
    examId
    agreement
    agree
    imageQualityScore
    qualityScore
    signedAt
    tags
    comment
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusReviewPull($input: PullEpocusReviewInput!) {
    pullEpocusReview(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusReview
      }
    }
  }
`;

gql`
  mutation SyncEpocusReviewPush($input: PushEpocusReviewInput!) {
    pushEpocusReview(input: $input) {
      rows {
        ...WholeSyncableEpocusReview
      }
    }
  }
`;
